import { useTranslation } from 'react-i18next';
import { Alert, Box, Typography } from '@mui/material';
import { RecommendedSetup } from 'features/blueprints/hooks/useGetEquipmentRecommendations';
import { Equipment } from 'hooks/useEquipment/types';
import { RentalSource } from '../utils/constants';

interface EquipmentValidationAlertsProps {
  recommendations: RecommendedSetup | null | undefined;
  // Mode-specific props
  mode?: 'create' | 'edit';
  // Rental sources (catalog vs manual)
  gensetRentalSource?: RentalSource;
  batteryRentalSource?: RentalSource;
  // Rental counts (number of units)
  gensetRentalCount?: number;
  batteryRentalCount?: number;
  // Manual input values
  gensetKvaManual?: number | string;
  batteryKwhManual?: number;
  batteryKwManual?: number;
  // Catalog mode: selected equipment from Step 3
  selectedGensetEquipment?: Equipment | null;
  selectedBatteryEquipment?: Equipment | null;
  // Filter alerts by type
  type?: 'genset' | 'battery' | 'all';
  // Responsive width constraints (matching ResponsiveFormContainer)
  maxColumnWidth?: number;
}

export const EquipmentValidationAlerts = ({
  recommendations,
  gensetRentalSource,
  batteryRentalSource,
  gensetRentalCount = 1,
  batteryRentalCount = 1,
  gensetKvaManual,
  batteryKwhManual,
  batteryKwManual,
  selectedGensetEquipment,
  selectedBatteryEquipment,
  type = 'all',
  maxColumnWidth,
}: EquipmentValidationAlertsProps) => {
  const { t } = useTranslation();

  if (!recommendations) {
    return null;
  }

  const alerts: { key: string; messages: string[] }[] = [];

  // Get all equipment values
  let gensetKva: number | undefined;
  let batteryKw: number | undefined;
  let batteryKwh: number | undefined;

  // Genset values (Step 3)
  if (gensetRentalSource === RentalSource.CATALOG && selectedGensetEquipment?.kvaValue) {
    gensetKva = selectedGensetEquipment.kvaValue;
  } else if (gensetRentalSource === RentalSource.MANUAL && gensetKvaManual) {
    gensetKva = typeof gensetKvaManual === 'string' ? parseFloat(gensetKvaManual) : gensetKvaManual;
  } else if (gensetRentalSource === RentalSource.NONE) {
    gensetKva = 0;
  }

  // Battery values (Step 3)
  if (batteryRentalSource === RentalSource.CATALOG && selectedBatteryEquipment) {
    batteryKw = selectedBatteryEquipment.kwPower ?? undefined;
    batteryKwh = selectedBatteryEquipment.kwhCapacity ?? undefined;
  } else if (batteryRentalSource === RentalSource.MANUAL) {
    batteryKw = batteryKwManual;
    batteryKwh = batteryKwhManual;
  }

  // Alert 1: Power requirement not met (kW, kVA - instant power delivery)
  // Calculate total power (single unit × count)
  const totalGensetKva = gensetKva != null && !isNaN(gensetKva) ? gensetKva * gensetRentalCount : undefined;
  const totalBatteryKw = batteryKw ? batteryKw * batteryRentalCount : undefined;

  // Genset power alert (only if type is 'genset' or 'all')
  if (
    (type === 'genset' || type === 'all') &&
    totalGensetKva != null &&
    totalGensetKva < recommendations.gensetMinKva
  ) {
    alerts.push({
      key: 'genset-power-requirement',
      messages: [
        t('blueprints:wizard.validations.gensetPowerInsufficient', {
          required: recommendations.gensetMinKva,
          current: totalGensetKva,
        }),
      ],
    });
  }

  // Battery power alert (only if type is 'battery' or 'all')
  if ((type === 'battery' || type === 'all') && totalBatteryKw && totalBatteryKw < recommendations.batteryMinKw) {
    alerts.push({
      key: 'battery-power-requirement',
      messages: [
        t('blueprints:wizard.validations.batteryConverterPowerInsufficient', {
          required: recommendations.batteryMinKw,
          current: totalBatteryKw,
        }),
      ],
    });
  }

  // Alert 2: Energy capacity requirement not met (kWh - energy storage)
  // Only for battery (only if type is 'battery' or 'all')
  if (type === 'battery' || type === 'all') {
    const totalBatteryKwh = batteryKwh ? batteryKwh * batteryRentalCount : undefined;

    if (totalBatteryKwh && totalBatteryKwh < recommendations.batteryMinKwh) {
      alerts.push({
        key: 'energy-capacity-requirement',
        messages: [
          t('blueprints:wizard.validations.batteryCapacityInsufficient', {
            required: recommendations.batteryMinKwh,
            current: totalBatteryKwh,
          }),
        ],
      });
    }
  }

  if (alerts.length === 0) {
    return null;
  }

  return (
    <Box
      data-testid="equipment-validation-alert"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        mb: 3,
        mt: 2.5,
        width: { xs: '100%', md: '50%', lg: `min(100%, ${maxColumnWidth}px)` },
      }}
    >
      {alerts.map((alert) => (
        <Alert
          key={alert.key}
          severity="warning"
          sx={{
            width: '100%',
            '& .MuiAlert-icon': {
              alignItems: 'center',
              display: 'flex',
            },
          }}
        >
          {alert.messages.length === 1 ? (
            <span
              dangerouslySetInnerHTML={{
                __html: alert.messages[0],
              }}
            />
          ) : (
            <Box>
              {alert.messages.map((msg, idx) => (
                <Typography
                  key={idx}
                  variant="body2"
                  sx={{ mb: idx < alert.messages.length - 1 ? 1 : 0 }}
                  dangerouslySetInnerHTML={{
                    __html: `• ${msg}`,
                  }}
                />
              ))}
            </Box>
          )}
        </Alert>
      ))}
    </Box>
  );
};
